import React, { useState, useRef, useEffect } from 'react';

/**
 * Кастомный select в стиле NovaFlo.
 * Native <select> в HTML использует OS-renderer — стилизовать его нельзя.
 *
 * Props:
 *   value: текущее значение
 *   onChange: (value) => void
 *   options: [{ value, label }] | [{ value, label, group? }]
 *   placeholder: текст когда нет выбора
 *   field: размер обычного поля формы (.form-input) — в карточках рядом с текстовыми полями
 */
function Select({ value, onChange, options = [], placeholder = '— Не выбрано —', disabled = false, style = {}, field = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  // Закрытие при клике вне
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Закрытие на Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      // preventDefault — Escape закрыл список, окно (Modal) под ним не закрываем
      if (e.key === 'Escape') { e.preventDefault(); setIsOpen(false); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const selectedOption = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={wrapRef} className={`nv-select${field ? ' nv-select-field' : ''}`} style={style}>
      <button
        type="button"
        className={`nv-select-trigger${isOpen ? ' open' : ''}${!selectedOption ? ' placeholder' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="nv-select-value">{displayLabel}</span>
        <svg className="nv-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="nv-select-menu" role="listbox">
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <button
                key={`${opt.value}-${opt.label}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`nv-select-option${isSelected ? ' selected' : ''}`}
                onClick={() => { onChange(opt.value); setIsOpen(false); }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Select;
